import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { playerInitial } from "../lib/playerColors";
import { ContinuerTag } from "./game/ContinuerTag";
import { t, tList, tRich, useLang } from "../lib/i18n";
import { useDesktop } from "../lib/desktop";
import { RankEmblem } from "./rank/RankEmblem";
import { TurnRing } from "./game/TurnRing";

/**
 * Opponent seated around the table — COMPACT VIEW.
 *
 * The opponent's posed combinations are NOT rendered here: they already appear
 * in the central TableCombinations row alongside everyone else's combos. This
 * avoids the visual duplication (KO zone) seen in the previous layout.
 *
 * The pose the opponent is composing is not shown here either: `PosePreview`
 * shows it centred at the top of the table, with the player's name (under the
 * pill it widened the column and pushed the other players' pills aside).
 */
export const OpponentDisplay = ({ player, isActive, clock = null, isContinuer = false, position = "top", testId, color, isHost, onReplaceWithBot, onOpenProfile }) => {
  const { dir } = useLang();
  // A computer's large screen: a bigger avatar, and the name and count to match (lib/desktop.js).
  const big = useDesktop();
  // Watching a rewarded video for a table colour (POST /ad-break): shown instead
  // of « Déco », and nobody may replace them meanwhile (owner's rule). Once the
  // video ends, a player still away is a disconnected one again: the button comes.
  const watchingAd = !!player.watching_ad && !player.is_bot && !player.has_quit;
  const canBeReplaced =
    isHost && !player.is_bot && !player.eliminated && !watchingAd && (player.has_quit || player.is_connected === false);
  // The server's hand_count only drops when the pose is committed (the discard):
  // the cards of the groups and additions being composed (preview_state) have
  // already left the hand on that player's screen, so they leave it here too.
  const preview = player.preview_state;
  const staged = [...(preview?.groups || []), ...(preview?.additions || [])]
    .reduce((s, g) => s + (g.cards?.length ?? 0), 0);
  const handCount = Math.max(0, player.hand_count + (preview?.took_discard ? 1 : 0) - staged);

  return (
    <div className="flex flex-col items-center gap-1.5 min-w-0 max-w-full" data-testid={testId}>
      {canBeReplaced && (
        <button
          onClick={() => onReplaceWithBot?.(player.user_id)}
          className="text-[9px] font-semibold uppercase tracking-wide bg-orange-500/20 hover:bg-orange-500/35 border border-orange-500/40 text-orange-300 hover:text-orange-200 rounded-full px-2 py-0.5 transition-colors cursor-pointer"
        >
          {t("opp.replaceBot")}
        </button>
      )}
      <div
        id={`opponent-avatar-${player.user_id}`}
        // A human's pill opens their card (PlayerCardDialog); bots have none.
        {...(onOpenProfile && !player.is_bot ? {
          role: "button",
          tabIndex: 0,
          title: t("players.open", { name: player.name }),
          onClick: () => onOpenProfile(player.user_id),
          onKeyDown: (e) => { if (e.key === "Enter") onOpenProfile(player.user_id); },
        } : {})}
        className={`flex items-center max-w-full rami-glass rounded-full ${onOpenProfile && !player.is_bot ? "cursor-pointer" : ""} ${big ? "gap-2.5 px-3 py-1.5" : "gap-1.5 min-[400px]:gap-2 px-2 min-[400px]:px-2.5 py-1"} ${
          isContinuer
            ? player.has_played_nemna3
              ? "continuer-green"
              : "continuer-red"
            : isActive
            ? "turn-pulse border-amber-400/80"
            : ""
        } ${player.eliminated ? "opacity-50 grayscale" : ""}`}
      >
        <span id={`player-icon-${player.user_id}`} className="relative shrink-0">
          <Avatar
            className={`${big ? "h-11 w-11" : "h-7 w-7"}${isActive && !player.eliminated && !clock ? " avatar-heartbeat" : ""}`}
            style={color ? { boxShadow: `0 0 0 2px ${color.border}` } : { boxShadow: "0 0 0 1px rgba(251,191,36,0.3)" }}
          >
            <AvatarImage src={player.picture} />
            <AvatarFallback
              className={`${big ? "text-[15px]" : "text-[11px]"} font-bold`}
              style={color ? { background: color.bg, color: color.hex } : { background: "rgba(6,78,59,0.8)", color: "#FCD34D" }}
            >
              {playerInitial(player)}
            </AvatarFallback>
          </Avatar>
          {/* Their turn's time (TurnRing), while the game waits for them. */}
          <TurnRing clock={clock} size={big ? 44 : 28} />
          {/* The player's rank on their avatar's corner (lib/ranks.js); bots have none. */}
          {player.rank && (
            <RankEmblem
              rank={player.rank}
              size={big ? 20 : 14}
              className={`absolute ${big ? "-bottom-1 -right-1.5" : "-bottom-1 -right-1"} drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]`}
            />
          )}
        </span>
        <div className="min-w-0">
          <div className={`text-white font-semibold truncate flex items-center gap-1 ${big ? "text-[14px] max-w-[180px]" : "text-[11px] max-w-[84px] min-[400px]:max-w-[100px] sm:max-w-[140px]"}`}>
            {/* The name gives way to the tags (BOT, Déco, Pub couleur), which always show whole. */}
            <span className="truncate min-w-0">{player.name}</span>
            {/* An empty list in a dictionary means "no bot tag" in that language. Hidden below 400 px: the name needs the room. */}
            {player.is_bot && tList("opp.bot")[0] && (
              <span className="hidden min-[400px]:inline shrink-0 text-[8px] bg-emerald-500/30 text-emerald-200 px-1 py-0.5 rounded-full">{tList("opp.bot")[0]}</span>
            )}
            {/* Let two turns in a row run out: short turns until they play again (the turn timer). */}
            {clock?.away && !player.has_quit && (
              <span title={t("opp.awayTitle")} className="shrink-0 text-[8px] bg-slate-500/25 text-slate-200 px-1 py-0.5 rounded-full whitespace-nowrap">
                {t("opp.away")}
              </span>
            )}
            {watchingAd ? (
              <span
                title={t("opp.watchingAdTitle")}
                className="shrink-0 text-[8px] bg-amber-500/20 text-amber-200 px-1 py-0.5 rounded-full whitespace-nowrap"
              >
                {t("opp.watchingAd")}
              </span>
            ) : (
              player.is_connected === false && !player.is_bot && (
                <span className="shrink-0 text-[8px] bg-red-500/20 text-red-300 px-1 py-0.5 rounded-full">{t("opp.offline")}</span>
              )
            )}
          </div>
          <div className={`text-amber-300/80 leading-tight flex items-center gap-1 flex-wrap ${big ? "text-[12px]" : "text-[10px]"}`}>
            <span dir={dir}>
              {tRich("opp.cards", { n: <b className="text-amber-200">{handCount}</b> }, { n: handCount })}
            </span>
            {player.preview_state?.took_discard && (
              <span className="text-[8px] text-amber-400/80 bg-amber-500/10 border border-amber-500/20 px-1 py-px rounded-full">
                {t("opp.fausse")}
              </span>
            )}
            {player.eliminated && t("opp.elimSuffix")}
          </div>
        </div>
      </div>

      {/* Said "Continuer" at the redeal vote: « شدّ الفريش » under the player's pill. */}
      {isContinuer && !player.eliminated && (
        <ContinuerTag posed={player.has_played_nemna3} className="-mt-0.5" />
      )}

    </div>
  );
};

export default OpponentDisplay;
